import { Service } from '@toeverything/infra';

import type { DocsService } from '../../doc';
import type { DocDisplayMetaService } from '../../doc-display-meta';
import type { FolderNode } from '../entities/folder-node';

export class DocTitleUniquenessService extends Service {
  constructor(
    private readonly docsService: DocsService,
    private readonly docDisplayMetaService: DocDisplayMetaService
  ) {
    super();
  }

  /**
   * 获取指定文件夹节点下所有文档的标题
   */
  private getDocTitlesInFolder(folderNode: FolderNode): string[] {
    const children = folderNode.sortedChildren$.value;
    const docTitles: string[] = [];

    for (const child of children) {
      const childInfo = child.info$.value;
      if (childInfo?.type === 'doc') {
        const docId = childInfo.data;
        const docTitle = this.docDisplayMetaService.title$(docId).value;
        docTitles.push(docTitle || '');
      }
    }

    return docTitles;
  }

  /**
   * 生成唯一的文档标题
   * @param baseTitle 基础标题
   * @param existingTitles 已存在的标题列表
   * @returns 唯一的标题
   */
  private generateUniqueTitle(
    baseTitle: string,
    existingTitles: string[]
  ): string {
    // 如果基础标题不存在冲突，直接返回
    if (!existingTitles.includes(baseTitle)) {
      return baseTitle;
    }

    // 检查是否已经有数字后缀
    const numberSuffixRegex = /^(.+?)\s*\((\d+)\)$/;
    const match = baseTitle.match(numberSuffixRegex);

    let baseName: string;
    let startNumber = 1;

    if (match) {
      // 如果已经有数字后缀，提取基础名称和起始数字
      baseName = match[1].trim();
      startNumber = parseInt(match[2], 10);
    } else {
      // 如果没有数字后缀，使用原标题作为基础名称
      baseName = baseTitle;
    }

    // 寻找可用的数字后缀
    let counter = startNumber;
    let newTitle: string;

    do {
      newTitle = `${baseName} (${counter})`;
      counter++;
    } while (existingTitles.includes(newTitle));

    return newTitle;
  }

  /**
   * 确保文档在指定文件夹中的标题唯一性
   * @param docId 文档ID
   * @param folderNode 目标文件夹节点
   * @param proposedTitle 建议的标题（可选）
   * @returns 最终使用的唯一标题
   */
  async ensureUniqueTitleInFolder(
    docId: string,
    folderNode: FolderNode,
    proposedTitle?: string
  ): Promise<string> {
    let uniqueTitle = this.ensureUniqueTitleInFolderWithoutChange(
      docId,
      folderNode,
      proposedTitle
    );
    // 如果标题发生了变化，更新文档标题
    // if (uniqueTitle !== currentTitle) {
    let newTitleArray = uniqueTitle.split('/');
    if (newTitleArray.length > 1) {
      // 发布标题无效事件
      const invalidTitleEvent = new CustomEvent('title-invalid');

      // 在全局范围内分发事件
      if (typeof window !== 'undefined') {
        window.dispatchEvent(invalidTitleEvent);
      }

      uniqueTitle = newTitleArray.join('');
    }
    await this.docsService.changeDocTitle(docId, uniqueTitle);
    // }

    return uniqueTitle;
  }

  ensureUniqueTitleInFolderWithoutChange(
    docId: string,
    folderNode: FolderNode,
    proposedTitle?: string
  ): string {
    // 获取当前文档的标题或使用建议的标题
    const currentTitle =
      proposedTitle ||
      this.docDisplayMetaService.title$(docId).value ||
      'Untitled';

    // 获取文件夹中已存在的文档标题（排除当前文档）
    const existingTitles = this.getDocTitlesInFolder(folderNode).filter(
      (title, index) => {
        const children = folderNode.sortedChildren$.value;
        const child = children[index];
        return child?.info$.value?.data !== docId; // 排除当前文档
      }
    );

    // 生成唯一标题
    const uniqueTitle = this.generateUniqueTitle(currentTitle, existingTitles);
    return uniqueTitle;
  }

  /**
   * 为新建文档生成默认标题
   * @param folderNode 目标文件夹节点
   * @param baseTitle 基础标题，默认为 "New page"
   * @returns 唯一的默认标题
   */
  generateDefaultTitle(folderNode: FolderNode, baseTitle = 'New page'): string {
    const existingTitles = this.getDocTitlesInFolder(folderNode);
    return this.generateUniqueTitle(baseTitle, existingTitles);
  }
}
